/**
 * Admin sections and their page ids, in strip order. This is the light,
 * copy-free half of the admin routing truth: App.tsx imports it eagerly (via
 * adminLocation.ts) to resolve URLs before the lazy admin shell loads, so it
 * holds ids only. adminNavRegistry.ts adds the labels, groups, descriptions
 * and keywords, and adminPages.tsx the page renderers; adminNavRegistry.test.ts
 * asserts all three list exactly the same (section, subtab) pairs.
 */
export type AdminSection =
  | "home"
  | "orders"
  | "providers"
  | "partners"
  | "money"
  | "countries"
  | "apps"
  | "system";

export const ADMIN_SECTION_PAGES: Readonly<Record<AdminSection, ReadonlyArray<string>>> = {
  home: ["overview", "switches"],
  orders: ["orders", "fulfillment", "feedback", "guest-support", "doodles-support", "bulk-orders", "doodles-accounts", "doodles-recovery"],
  providers: ["directory", "awaiting", "operations", "quality"],
  partners: ["directory", "applications", "app-launches", "controls", "referral-fees"],
  money: [
    "payouts", "wise-payouts", "payout-runs", "settlement",
    "fees", "coupons", "pricing", "model-simulator", "forex",
    "tax-report", "finance"
  ],
  countries: ["country-launch", "coverage", "policy", "bubbles", "lanes"],
  apps: ["app-catalog", "traffic", "doodles-config", "matching-config", "matching-weights", "standard-colors", "first-party-keys"],
  system: [
    "health", "app-status", "audit",
    "maintenance", "announcements", "legal-documents",
    "enforcement", "storage-cleanup", "data-retention"
  ]
};

/** Top-strip order. The first section is the admin landing page. */
export const ADMIN_SECTIONS = Object.keys(ADMIN_SECTION_PAGES) as ReadonlyArray<AdminSection>;

export const ADMIN_DEFAULT_SECTION: AdminSection = "home";

export function isAdminSection(value: string | null | undefined): value is AdminSection {
  return typeof value === "string" && Object.prototype.hasOwnProperty.call(ADMIN_SECTION_PAGES, value);
}

/** The page a section opens on: its first page. */
export function defaultAdminSubtab(section: AdminSection): string {
  return ADMIN_SECTION_PAGES[section][0]!;
}

export function isAdminDestination(section: string, subtab: string): boolean {
  return isAdminSection(section) && ADMIN_SECTION_PAGES[section].includes(subtab);
}
